import { HttpStatusCode } from 'axios';
import httpClient from '@common/api/httpClient';
import { getFileName } from '@common/api/rest/helpers';
import {
  type DataManagementExportRequest,
  type DataManagementExportResponse,
  type Download,
  type ExportRequest,
  type ExportStatus,
  type ExportStatusResponse,
} from '@common/types/api';

// "Maximum number of exports reached" is the only 400 on this endpoint (swagger)
const nameExportLimit = (status: number) =>
  status === HttpStatusCode.BadRequest ? { kind: 'rate-limited' as const } : undefined;

/**
 * @throws {ApiError} `rate-limited` when the maximum number of exports is reached; the server
 * reports that as 400 on this endpoint.
 */
export async function requestCsvExport(
  request: ExportRequest,
): Promise<string> {
  const { data } = await httpClient.post<{ id: string; }>(
    '/rest/v1/DataHistory/Exports',
    request,
    { nameError: nameExportLimit },
  );
  return data.id;
}

export async function checkCsvExportStatus(
  id: string,
) {
  const { data } = await httpClient.get<ExportStatusResponse>(
    `/rest/v1/DataHistory/Exports/${id}/Status`,
  );
  return data.status;
}

export async function downloadCsvExport(
  id: string,
): Promise<Download> {
  const response = await httpClient.get<Blob>(
    `/rest/v1/DataHistory/Exports/${id}/Download`,
    {
      responseType: 'blob',
    },
  );
  const filename = getFileName(response.headers['content-disposition'])
    ?? (response.data?.type?.toLowerCase().includes('csv') ? 'CSV_export.csv' : 'CSV_export.zip');

  return {
    filename,
    content: response.data,
  };
}

/** @throws {ApiError} `rate-limited` when the user has started too many exports recently (429). */
export async function requestDataManagementCsvExport(
  request: DataManagementExportRequest,
): Promise<string> {
  const { data } = await httpClient.post<DataManagementExportResponse>(
    '/rest/v1/DataManagement/DataHistory/Exports',
    request,
  );
  return data.id;
}

export async function checkDataManagementCsvExportStatus(
  id: string,
): Promise<ExportStatus> {
  const { data } = await httpClient.get<ExportStatusResponse>(
    `/rest/v1/DataManagement/DataHistory/Exports/${id}/Status`,
  );
  return data.status;
}

export async function downloadDataManagementCsvExport(
  id: string,
): Promise<Download> {
  const response = await httpClient.get<Blob>(
    `/rest/v1/DataManagement/DataHistory/Exports/${id}/Download`,
    {
      responseType: 'blob',
    },
  );
  const filename = getFileName(response.headers['content-disposition'])
    ?? (response.data?.type?.toLowerCase().includes('csv') ? 'CSV_export.csv' : 'CSV_export.zip');

  return {
    filename,
    content: response.data,
  };
}
